import { date } from 'quasar'
import { OPERATIONS, STAGES } from 'src/models/kitchen-display/KitchenDisplayType'
import { ORDER_ITEM_STATES } from 'src/models/order-item/OrderItem'
import OrderActionsDialog from 'src/modules/kds/dialogs/OrderActionsDialog'
const { addToDate } = date
import { IsoDateTime, msPerMinute } from 'src/models/model'
/**
 * This mixin is used to create kitchen card components that share styling and kitchen action functionality.
 */
export default {
  context: ['currentUser'],
  configuration: ['currentSite'],
  inject: ['kitchenDisplayType'],
  props: {
    index: {
      type: Number,
      required: false,
      default: 1
    },
    kitchenDisplay: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    orderItems: {
      type: Array,
      required: true
    },
    tab: {
      type: String,
      required: true
    },
    showPreparationCounter: {
      type: Boolean,
      required: false,
      default: false
    },
    deliveryTypeFilter: {
      type: String,
      required: false,
      default: null
    },
    selected: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    orderItem () {
      return null
    },
    actionableOrderItems () {
      return _.filter(this.orderItems, orderItem => {
        return !orderItem.isHeld && orderItem.state !== ORDER_ITEM_STATES.VOIDED
      })
    },
    hotstartOrderItems () {
      if (this.currentUser.superUser) {
        return _.filter(this.orderItems, orderItem => {
          return orderItem.state !== ORDER_ITEM_STATES.VOIDED
        })
      } else {
        return this.actionableOrderItems
      }
    },
    actionButtons () {
      return _.compact([
        this.hotstartAction,
        this.printAction,
        this.showSendButton ? this.sendAction : null,
        this.showReturnButton ? this.returnAction : null,
        this.prepareAction,
        this.remakeAction,
        this.deliverAction,
        this.recallAction,
        this.dismissAction
      ])
    },
    showSendButton () {
      // Only show send if we don't swipe, or either prepare or deliver is not available
      // If we can swipe we default to send, so we don't need to show it as a button and clutter the dialog
      return !this.kitchenDisplayType?.showSwipeControls || !this.prepareAction || !this.deliverAction
    },
    showReturnButton () {
      // Only show send if we don't swipe, or either remake or recall is not available
      return !this.kitchenDisplayType?.showSwipeControls || !this.remakeAction || !this.recallAction
    },
    hotstartAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.hotstart) return null

      if (this.hotstartOrderItems.length === 0) return null
      if (_.includes([STAGES.COMPLETE], this.tab)) return null

      return {
        class: 'col-12',
        label: OPERATIONS.HOTSTART,
        icon: 'local_fire_department',
        iconRight: 'local_fire_department',
        iconSize: 'xs',
        color: 'warning',
        size: 'sm',
        dense: true,
        handler: () => this.hotstart(),
        enabled: _.some(this.hotstartOrderItems, orderItem => orderItem.isUnprepared)
      }
    },
    printAction () {
      return null
    },
    dismissAction () {
      const allOrderItemsVoided = !_.some(this.orderItems, orderItem => _.includes(ORDER_ITEM_STATES.NOT_VOIDED_STATES, orderItem.state))

      if (!allOrderItemsVoided) return null

      return {
        class: 'col-12',
        label: OPERATIONS.DISMISS,
        icon: 'eraser',
        iconRight: 'eraser',
        color: 'warning',
        enabled: true,
        handler: () => this.dismiss()
      }
    },
    sendAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.send) return null

      if (this.actionableOrderItems.length === 0) return null
      if (_.includes([STAGES.COMPLETE], this.tab)) return null

      return {
        class: 'col-6',
        label: OPERATIONS.SEND,
        iconRight: 'delivery_dining',
        iconSize: 'lg',
        color: 'positive',
        handler: () => this.send(),
        enabled: _.some(this.actionableOrderItems, orderItem => orderItem.isUndelivered)
      }
    },
    returnAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.return) return null

      if (this.actionableOrderItems.length === 0) return null

      return {
        class: 'col-6',
        label: OPERATIONS.RETURN,
        icon: 'no_food',
        iconClass: 'q-mb-xs',
        color: 'negative',
        handler: () => this.return(),
        enabled: _.some(this.actionableOrderItems, orderItem => orderItem.isPrepared)
      }
    },
    prepareAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.prepare) return null

      if (this.actionableOrderItems.length === 0) return null
      if (_.includes([STAGES.COMPLETE], this.tab)) return null

      return {
        class: 'col-6',
        label: OPERATIONS.PREPARE,
        iconRight: 'fastfood',
        iconClass: 'q-mb-xs',
        color: 'positive',
        handler: () => this.prepare(),
        enabled: _.some(this.actionableOrderItems, orderItem => orderItem.isUnprepared)
      }
    },
    remakeAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.remake) return null

      if (this.actionableOrderItems.length === 0) return null
      if (_.includes([STAGES.COMPLETE], this.tab)) return null

      return {
        class: 'col-6',
        label: OPERATIONS.REMAKE,
        icon: 'no_food',
        iconClass: 'q-mb-xs',
        color: 'negative',
        handler: () => this.remake(),
        enabled: !_.some(this.actionableOrderItems, orderItem => orderItem.isDelivered) && _.some(this.actionableOrderItems, orderItem => orderItem.isReadyForDelivery)
      }
    },
    deliverAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.deliver) return null

      if (this.actionableOrderItems.length === 0) return null
      if (_.includes([STAGES.PREPARE, STAGES.COMPLETE], this.tab)) return null

      return {
        class: 'col-6',
        label: OPERATIONS.DELIVER,
        iconRight: 'delivery_dining',
        iconSize: 'lg',
        color: 'positive',
        handler: () => this.deliver(),
        enabled: !_.some(this.actionableOrderItems, orderItem => orderItem.isUnprepared) && _.some(this.actionableOrderItems, orderItem => orderItem.isReadyForDelivery)
      }
    },
    recallAction () {
      if (!this.kitchenDisplayType?.deliveryOperations?.recall) return null

      if (this.actionableOrderItems.length === 0) return null
      if (_.includes([STAGES.PREPARE], this.tab)) return null

      return {
        class: 'col-6',
        label: OPERATIONS.RECALL,
        icon: 'delivery_dining',
        iconSize: 'lg',
        color: 'negative',
        handler: () => this.recall(),
        enabled: !_.some(this.actionableOrderItems, orderItem => orderItem.isUndelivered)
      }
    },
    canSwipeLeft () {
      if (!this.kitchenDisplayType.showSwipeControls) return false

      return _.some(this.actionableOrderItems, orderItem => orderItem.isPrepared)
    },
    canSwipeRight () {
      if (!this.kitchenDisplayType.showSwipeControls) return false

      return _.some(this.actionableOrderItems, orderItem => orderItem.isUndelivered)
    },
    orderCardStyles () {
      const dueTime = this.tab !== STAGES.COMPLETE
        ? _.first(this.orderItems)?.siteDueTime
        : _.last(this.orderItems)?.siteDueTime

      let color = this.currentSite.primaryColor
      if (!dueTime) {
        color = this.currentSite.warningColor
      } else if (this.tab !== STAGES.COMPLETE) {
        if (this.currentSite.siteTimeWithSeconds > dueTime) {
          color = this.currentSite.negativeColor
        } else if (Math.floor(Math.abs((this.currentSite.siteTimeWithSeconds - dueTime) / msPerMinute)) < this.longestPrepTime) {
          color = this.currentSite.positiveColor
        } else if (!!this.prepOffsetDuration && this.prepOffsetDuration < this.prepMsLimit) {
          color = this.$lightenColor(this.currentSite.infoColor, -20)
        } else {
          color = this.currentSite.infoColor
        }
      } else {
        const deliveredTime = _.last(this.orderItems).deliveredTime

        if (deliveredTime > addToDate(dueTime, { minutes: this.kitchenDisplayType.deliveryWindowMinutes })) {
          color = this.currentSite.negativeColor
        } else if (deliveredTime < addToDate(dueTime, { minutes: this.kitchenDisplayType.deliveryWindowMinutes * -1 })) {
          color = this.currentSite.infoColor
        } else {
          color = this.currentSite.infoColor
        }
      }

      const backgroundColor = this.index % 2 === 1 ? this.$lightenColor(color, this.$isColorDark(color) ? 5 : -5) : color
      return {
        background: backgroundColor,
        color: this.$isColorDark(backgroundColor) ? 'white' : 'black'
      }
    },
    longestPrepTimeOrderItem () {
      return this.orderItem || _(this.orderItems).sortBy(orderItem => !orderItem.item.requiresPreparation || orderItem.isPrepared || orderItem.isHeld ? 1 : -orderItem.item.preparationTimeMinutes).first()
    },
    longestPrepTime () {
      return this.longestPrepTimeOrderItem?.itemOrderItem?.item?.preparationTimeMinutes || 0
    },
    readyToPrep () {
      return this.showPreparationCounter && !!this.prepOffsetDuration && this.prepOffsetDuration < 0
    },
    prepTime () {
      if (
        !this.kitchenDisplayType.showPreparationCountdown ||
        this.longestPrepTimeOrderItem.isPrepared ||
        this.longestPrepTimeOrderItem.isHeld ||
        !this.longestPrepTimeOrderItem.item.requiresPreparation
      ) {
        return null
      }

      return IsoDateTime.parse(addToDate(this.longestPrepTimeOrderItem.dueTime, { minutes: -this.longestPrepTimeOrderItem.item?.preparationTimeMinutes })).inTimeZone(this.currentSite.timeZone)
    },
    prepMsLimit () {
      if (!this.prepTime) return null

      return (this.longestPrepTimeOrderItem.item?.preparationTimeMinutes + this.kitchenDisplayType.deliveryWindowMinutes) * msPerMinute
    },
    prepOffsetDuration () {
      if (!this.prepTime) return null

      return this.prepTime - this.currentSite.siteTimeWithSeconds
    },
    relativePrepTime () {
      if (!this.prepTime) return null

      if (this.prepOffsetDuration < msPerMinute || this.prepOffsetDuration > this.prepMsLimit) return null

      return this.prepTime.relativeTime({
        relativeTo: this.currentSite.siteTimeWithSeconds,
        duration: true,
        label: false,
        msPrecisionLimit: this.prepMsLimit
      })
    }
  },
  emits: ['refresh', 'select', 'deselect'],
  methods: {
    // ACTIONS
    async handleSwipeLeft (reset = null) {
      if (_.isFunction(reset)) reset()

      let areItemsProcessed = false

      // If return is permitted, assume swipes are for returning
      if (this.returnAction?.enabled) {
        areItemsProcessed = this.returnAction.handler()
        // If we can't send, assume remake first - remake is more specific than recall
      } else if (!areItemsProcessed && this.remakeAction?.enabled) {
        areItemsProcessed = this.remakeAction.handler()
      }

      if (this.recallAction?.enabled) {
        areItemsProcessed = this.recallAction.handler()
      }
    },
    async handleSwipeRight (reset = null) {
      if (_.isFunction(reset)) reset()

      let areItemsProcessed = false

      // If send is permitted, assume swipes are for sending
      if (this.sendAction?.enabled) {
        areItemsProcessed = this.sendAction.handler()
      // If we can't send, assume delivery first - deliver is more specific than prepare
      } else if (this.deliverAction?.enabled) {
        areItemsProcessed = this.deliverAction.handler()
      }

      if (!areItemsProcessed && this.prepareAction?.enabled) {
        areItemsProcessed = this.prepareAction.handler()
      }
    },
    async hotstart () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isUnprepared, this.hotstartOrderItems)
      if (_.isEmpty(orderItemIds)) return false

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.HOTSTART)
      this.$emit('refresh', this.order.id)

      return true
    },
    async send () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isUndelivered)
      if (_.isEmpty(orderItemIds)) return true

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.SEND)
      this.$emit('refresh', this.order.id)

      return true
    },
    async return () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isPrepared)
      if (_.isEmpty(orderItemIds)) return true

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.RETURN)
      this.$emit('refresh', this.order.id)

      return true
    },
    async prepare () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isUnprepared)
      if (_.isEmpty(orderItemIds)) return false

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.PREPARE)
      this.$emit('refresh', this.order.id)

      return true
    },
    async remake () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isReadyForDelivery)
      if (_.isEmpty(orderItemIds)) return false

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.REMAKE)
      this.$emit('refresh', this.order.id)

      return true
    },
    async deliver () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isReadyForDelivery)
      if (_.isEmpty(orderItemIds)) return false

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.DELIVER)
      this.$emit('refresh', this.order.id)

      return true
    },
    async recall () {
      const orderItemIds = this.getOrderItemIdsForAction(orderItem => orderItem.isDelivered)
      if (_.isEmpty(orderItemIds)) return false

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.RECALL)
      this.$emit('refresh', this.order.id)

      return true
    },
    async dismiss () {
      const orderItemIds = _(this.orderItems).filter({ state: ORDER_ITEM_STATES.VOIDED }).map('id').value()
      if (_.isEmpty(orderItemIds)) return false

      await this.kitchenDisplay.updateOrderForKitchenDisplay(this.order.id, orderItemIds, OPERATIONS.DISMISS)
      this.$emit('refresh', this.order.id)

      return true
    },
    getOrderItemIdsForAction (passes, orderItems = this.actionableOrderItems) {
      let orderItemIds = []
      _.each(orderItems, orderItem => {
        if (passes(orderItem)) {
          orderItemIds.push(orderItem.id)
        } else {
          orderItemIds = [] // abort action
          return false // break each early
        }
      })
      return orderItemIds
    },
    promptOrderActions () {
      this.$emit('select')
      this.$q.dialog({
        component: OrderActionsDialog,
        componentProps: {
          actions: this.actionButtons,
          onDismiss: () => this.$emit('deselect')
        }
      })
    }
  }
}
